import React from 'react';
import styled, { DefaultTheme } from 'styled-components';
import loadable from '@loadable/component';
import { forDesktop, forIE, maxedPageWrapperStyle } from '@westwing/ui-kit/breakPoints';
import WestwingNowLogo from '@westwing/ui-kit/Icons/WestwingNow';
import useConfig from '../../hooks/useConfig';
import BurgerMenuIcon from '../Generic/Icons/Svgs/Burger';
import HybridLink from '../Generic/Links/HybridLink';
import CategorySeoLinks from '../SeoLinks/CategorySeoLinks';
import { HeaderBarBannerData } from '../../../common/apiAdapters/types';
import { VictorQaClasses } from '../../../common/qaIds';
import useFeatureToggle from '../../hooks/useFeatureToggle';
import IconsButtonTray from './IconsButtonTray';
import ShopTab from './ShopTab';
import ClubTab from './ClubTab';
import InfoSection from './InfoSection';
import Navigation from './Navigation';

const Search = loadable(() => import('./Search'));
const DynamicSearch = loadable(() => import('./DynamicSearch'));

const OneHeaderWrapper = styled.div`
    position: relative;
`;

const OneHeaderLayout = styled.header<{ showHeaderTabs: boolean }>`
    ${maxedPageWrapperStyle}
    display: grid;
    background: white;
    grid-template:
        'tabs   tabs         tabs    tabs    tabs    tabs' ${({ showHeaderTabs }) => (showHeaderTabs ? '46' : '0')}px
        'burger search       logo    logo    icons   icons ' ${({ theme }) => theme.oneHeaderModuleHeight}px
        / 35px 40px 1fr 1fr 40px 35px;

    ${forDesktop`
        /* safari 10.1 does not support minmax inside grid-templaete so this first grid-template rule is a fallback for safari 10.1 */
        grid-template:
            '  .                           tabs      tabs   tabs    tabs      .        info    info    '  46px
            '  .                           logo      logo   .       search    search   search  icons   '  103px
            / ${({ theme }) =>
                theme.desktopPagePadding}   188px        41px     79px       68px        1fr      88px   323px    ;
        grid-template:
            '  .                           tabs      tabs    tabs    tabs     .        info    info    '  46px
            '  .                           logo      logo    .       search   search   search  icons   '  103px
            / ${({ theme }) =>
                theme.desktopPagePadding}   188px        41px     79px       68px        1fr      minmax(88px, max-content)   323px    ;

        height: 149px;
    `}
    ${forIE`
        display: -ms-grid;
        -ms-grid-rows: 46px 103px;
        -ms-grid-columns: ${({ theme }: { theme: DefaultTheme }) =>
            theme.desktopPagePadding} 188px        41px     79px       68px        1fr      minmax(88px, max-content)   323px;
        background: transparent;
    `}
`;

const HeaderTabsWrapper = styled.div<{ showHeaderTabs: boolean }>`
    grid-area: tabs;
    display: ${({ showHeaderTabs }) => (showHeaderTabs ? 'flex' : 'none')};
    border-top: 2px solid ${({ theme }) => theme.colors.turquoise100};
    ${forDesktop`
        border-top: none;
        display: flex;
    `}
    ${forIE`
        width: 376px;
        display: flex;
    `}
`;

const OutsideHeaderBackground = styled.div`
    display: none;
    ${forDesktop`
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 46px;
        display: block;
        background-color: ${({ theme }) => theme.colors.turquoise20};
    `}
`;

const BigLogo = styled(HybridLink)`
    display: block;
    grid-area: logo;
    ${forDesktop`
        background: ${({ theme }) => theme.colors.white};
        display: flex;
        align-items: center;
    `}

    ${forIE`
        -ms-grid-row: 2;
        -ms-grid-column: 2;
        -ms-grid-column-span: 2;
    `}
`;

const StyledWestwingNowLogo = styled(WestwingNowLogo)`
    width: 160px;
    margin: auto;
    height: 100%;
    display: block;

    ${forDesktop`
        width: 100%;
    `}
`;

const IconWrapper = styled.div`
    grid-area: burger;
    padding: 13px 0 0 13px;
    color: ${({ theme }) => theme.colors.charcoal100};
    width: 30px;
    ${forDesktop`
        display: none;
    `}
`;

const StyledHeaderBackground = styled.div`
    grid-column: 1/-1;
    grid-row: 2/3;
    background: ${({ theme }) => theme.colors.white};
    ${forDesktop`
        display: block;
    `}

    ${forIE`
        display: none;
    `}
`;
const StyledHeaderTabsBackground = styled.div`
    display: none;
    grid-column: 1/-1;
    grid-row: 1/2;
    background: ${({ theme }) => theme.colors.turquoise100};
    ${forDesktop`
        display: block;
    `}

    ${forIE`
        display: none;
    `}
`;

interface Props {
    handleBurgerMenuIconClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    handleLogoClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    wishListCount: number;
    minOrderValue: number | null;
    headerBarBanner?: HeaderBarBannerData;
    showHeaderTabs?: boolean;
}

const OneHeader: React.SFC<Props> = ({
    handleBurgerMenuIconClick,
    handleLogoClick,
    minOrderValue,
    wishListCount,
    showHeaderTabs = true,
}) => {
    const {
        links: { home: HomeLink = '', myWishlist, cart, myAccount },
    } = useConfig();

    const { p100DynamicSearchSuggestions = false } = useFeatureToggle();

    return (
        <div>
            <OneHeaderWrapper>
                <OneHeaderLayout showHeaderTabs={showHeaderTabs}>
                    <OutsideHeaderBackground />
                    <StyledHeaderTabsBackground />
                    <HeaderTabsWrapper showHeaderTabs={showHeaderTabs}>
                        <ClubTab />
                        <ShopTab />
                    </HeaderTabsWrapper>
                    <StyledHeaderBackground />
                    <IconWrapper
                        className={VictorQaClasses.headerBurgerMenuIcon}
                        onClick={handleBurgerMenuIconClick}
                        data-testid="burger_menu_icon"
                    >
                        <BurgerMenuIcon />
                    </IconWrapper>
                    {p100DynamicSearchSuggestions ? <DynamicSearch /> : <Search />}
                    <CategorySeoLinks />
                    <IconsButtonTray
                        wishListItemCount={wishListCount}
                        wishListUrl={myWishlist}
                        cartUrl={cart}
                        myAccountUrl={myAccount}
                    />
                    <BigLogo to={HomeLink} onClick={handleLogoClick}>
                        <StyledWestwingNowLogo />
                    </BigLogo>
                    <InfoSection minOrderValue={minOrderValue} />
                </OneHeaderLayout>
            </OneHeaderWrapper>
            <Navigation />
        </div>
    );
};

export default OneHeader;
